/**
 * Public hook + cache for Google Business Profile (GBP) data.
 *
 * Source: GET /api/gbp (returns only active profiles).
 *
 * Provides:
 *   useGbpProfiles()           — full list, cached in-module
 *   useGbpForCity(citySlug)    — single profile by exact slug
 *   useGbpForPath(pathname)    — smart auto-mapping: looks for the first
 *                                 city slug appearing as a segment of the
 *                                 current URL (e.g. /home-tuition-bhopal,
 *                                 /bhopal, /anand-tutorials-bhopal all
 *                                 resolve to the Bhopal profile).
 *
 * Cache lives at module scope (lifetime = page session); the first hook to
 * mount triggers the fetch and all subsequent hooks reuse the result. Admin
 * mutations elsewhere call `invalidateGbpCache()` so the next consumer
 * re-fetches the fresh list.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

let _cachePromise = null;
let _cachedList = null;

function fetchProfiles() {
    if (_cachedList !== null) return Promise.resolve(_cachedList);
    if (_cachePromise) return _cachePromise;
    _cachePromise = api
        .get("/gbp")
        .then((r) => {
            _cachedList = Array.isArray(r.data) ? r.data : [];
            return _cachedList;
        })
        .catch(() => {
            _cachedList = [];
            return _cachedList;
        })
        .finally(() => {
            _cachePromise = null;
        });
    return _cachePromise;
}

export function invalidateGbpCache() {
    _cachedList = null;
    _cachePromise = null;
}

export function useGbpProfiles() {
    const [list, setList] = useState(_cachedList || []);
    const [loaded, setLoaded] = useState(_cachedList !== null);
    useEffect(() => {
        let alive = true;
        fetchProfiles().then((l) => {
            if (alive) {
                setList(l);
                setLoaded(true);
            }
        });
        return () => { alive = false; };
    }, []);
    return { profiles: list, loaded };
}

export function useGbpForCity(citySlug) {
    const { profiles, loaded } = useGbpProfiles();
    if (!citySlug) return { profile: null, loaded };
    const slug = String(citySlug).toLowerCase();
    return {
        profile: profiles.find((p) => p.city_slug === slug) || null,
        loaded,
    };
}

/**
 * Smart auto-mapping. Given a URL pathname, return the first matching active
 * GBP profile by checking whether any known city_slug is contained as a
 * path token. Handles /<city>, /home-tuition-<city>, /home-tutors-<city>,
 * /online-classes-<city>, /anand-tutorials-<city>, /post/<city>-... etc.
 */
export function useGbpForPath(pathname) {
    const { profiles, loaded } = useGbpProfiles();
    if (!pathname || !profiles.length) return { profile: null, loaded };
    const p = pathname.toLowerCase();
    // Prefer longest-slug match so "indore" doesn't match "indore-old" etc.
    const sorted = [...profiles].sort(
        (a, b) => (b.city_slug || "").length - (a.city_slug || "").length
    );
    for (const prof of sorted) {
        const s = prof.city_slug;
        if (!s) continue;
        // Match as a token boundary (start, end, or surrounded by - or /).
        const re = new RegExp(`(^|[/-])${s.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}($|[/?#-])`);
        if (re.test(p)) {
            return { profile: prof, loaded };
        }
    }
    return { profile: null, loaded };
}
